import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useI18nText } from '@/lib/utils/i18nText';
import { useCopyLink } from '@/lib/utils/useCopyLink';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { ChannelThumbnail } from '@/components/ChannelThumbnail';
import Tooltip from '@/components/Tooltip';
import { GgcCheckbox } from '@/components/ui/ggc/checkbox';
import { useNameColumnButtons, toggleNameColumnButton } from '@/lib/utils/nameColumnButtons';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { usePreviewMode } from '@/contexts/PreviewModeContext';
import { loadPanelUser } from '@/lib/api/panel.api';
import { showSnackbar } from '@/store/slices/snackbarSlice';
import { EditChannelDialog, type EditChannelDialogProps } from '@/components/EditChannelDialog';
import { CampaignHistoryPopover, loadCampaignHistoryItems } from '@/components/CampaignHistoryPopover';


type PopoverPlacement = 'bottom-start' | 'right-start';

interface ChannelLinksPopoverProps {
  userId: string;
  channelName?: string;
  channelType?: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
  placement?: PopoverPlacement;
  thumbnailUrl?: string;
  chatHref?: string;
  detailsHref?: string;
  onLinkClick?: () => void;
  /** 설정 시 「채팅 시스템」 클릭은 새 탭 대신 콜백만 호출 (대시보드 우측 드로어 등) */
  onChatSystemClick?: (userId: string) => void;
  /** 설정 시 「채널 상세」 클릭은 새 탭 대신 콜백만 호출 */
  onChannelDetailsClick?: (userId: string) => void;
  /** 설정 시 「통계 보기」 클릭은 새 탭 대신 콜백만 호출 (드로어 열기 용) */
  onStatsClick?: (userId: string) => void;
  /** 「채널 수정」 클릭 동작 override. 미설정 시 컴포넌트가 직접 유저를 로드해
   *  공통 EditChannelDialog를 연다 (모든 페이지에서 항목이 동일하게 노출됨).
   *  Promise를 반환하면 데이터 로딩이 끝날 때까지 항목에 로딩 스피너를 표시한다. */
  onChannelEditClick?: (userId: string) => void | Promise<void>;
  /** 「채널 수정」의 기본 EditChannelDialog 저장 후 호출 (목록 새로고침 등) */
  onChannelSaved?: () => void;
  /** `click`: 트리거(자식) 클릭으로만 열림 · 호버로는 열리지 않음 (기본 `hover`) */
  openTrigger?: 'hover' | 'click';
  /** 소셜/채널 플랫폼 링크 목록 (제공 시 팝오버에 표시) */
  socialLinks?: Array<{ type?: string; url?: string }>;
  /** true 이면 기본 링크(채팅 시스템·채널 상세)를 숨김 */
  hideDefaultLinks?: boolean;
  /** 알고 있으면 「캠페인 이력」 항목 오른쪽에 개수를 바로 표시 (없으면 클릭 시 로드해서 연다) */
  campaignHistoryCount?: number;
}

export default function ChannelLinksPopover({
  userId,
  channelName,
  channelType,
  subtitle,
  children,
  className,
  placement = 'bottom-start',
  thumbnailUrl,
  chatHref,
  detailsHref,
  onLinkClick,
  onChatSystemClick,
  onChannelDetailsClick,
  onStatsClick,
  onChannelEditClick,
  onChannelSaved,
  openTrigger = 'hover',
  socialLinks,
  hideDefaultLinks = false,
  campaignHistoryCount,
}: ChannelLinksPopoverProps) {
  const { t } = useI18nText();
  const copyLink = useCopyLink();
  const dispatch = useAppDispatch();
  // Internal admin actions (Chat / Details / Edit / Stats) are hidden for the
  // advertiser preview and guest (non-admin) sessions — only a real admin outside
  // preview sees them. Centralized here so every caller behaves the same.
  const isPreview = usePreviewMode();
  const role = useAppSelector((s) => s.envs.currentUser?.role);
  // Edit-dialog user loaded on demand when no onChannelEditClick override is given.
  const [editUser, setEditUser] = useState<EditChannelDialogProps['user'] | null>(null);
  // Global preference for which of these actions appear as quick buttons under the channel name.
  const nameColPrefs = useNameColumnButtons();
  const toggleTip = t('Check to keep this button shown in the Name column', '체크하면 채널명 컬럼에 이 버튼을 계속 표시합니다');
  const [open, setOpen] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  // Keep the popover open with a spinner while the edit dialog's data loads.
  const [editLoading, setEditLoading] = useState(false);

  const handleCopyUrl = useCallback((e: MouseEvent, url: string) => {
    e.preventDefault();
    e.stopPropagation();
    copyLink(url);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 1500);
  }, [copyLink]);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const portalFloatRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const portalHostRef = useRef<HTMLDivElement | null>(null);
  const [portalReady, setPortalReady] = useState(false);
  const resolvedChatHref = chatHref ?? `/influencer/${userId}/campaigns`;
  const resolvedDetailsHref = detailsHref ?? `/panel/user/${userId}`;
  const isClickMode = openTrigger === 'click';
  const primarySocialUrl = socialLinks?.find((l) => l.url)?.url;
  const showInternalActions = role === 'admin' && !isPreview && !hideDefaultLinks;

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;

    const gap = 8;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    // Use the rendered popover size when available; fall back to an estimate
    // for the first frame (portal not yet mounted on initial open).
    const floatRect = portalFloatRef.current?.getBoundingClientRect();
    const popoverH = floatRect?.height || 220;
    const popoverW = floatRect?.width || 240;

    if (placement === 'right-start') {
      // Flip to the left side of the trigger when it would overflow the right edge.
      let left = rect.right + gap;
      if (left + popoverW > viewportW - gap && rect.left - gap - popoverW >= 0) {
        left = rect.left - gap - popoverW;
      }
      // Keep the popover within the viewport vertically.
      let top = rect.top;
      if (top + popoverH > viewportH - gap) {
        top = Math.max(gap, viewportH - gap - popoverH);
      }
      setPosition({ top, left });
      return;
    }

    // bottom-start: flip above the trigger when there isn't room below
    // but there is room above (prevents bottom-row clipping).
    let top = rect.bottom + gap;
    if (top + popoverH > viewportH - gap) {
      const aboveTop = rect.top - gap - popoverH;
      top = aboveTop >= gap ? aboveTop : Math.max(gap, viewportH - gap - popoverH);
    }
    // Keep the popover within the viewport horizontally.
    let left = rect.left;
    if (left + popoverW > viewportW - gap) {
      left = Math.max(gap, viewportW - gap - popoverW);
    }
    setPosition({ top, left });
  }, [placement]);

  const handleOpen = useCallback(() => {
    clearCloseTimer();
    updatePosition();
    setOpen(true);
  }, [clearCloseTimer, updatePosition]);

  const handleCloseSoon = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, 120);
  }, [clearCloseTimer]);

  useLayoutEffect(() => {
    if (!open) return;
    // Re-measure with the mounted portal so the flip uses the real height (no flicker).
    updatePosition();
    const handleViewportChange = () => updatePosition();
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);
    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open || !isClickMode) return;
    const onDocDown = (e: globalThis.MouseEvent) => {
      const node = e.target as Node;
      if (wrapRef.current?.contains(node)) return;
      if (portalFloatRef.current?.contains(node)) return;
      // The "Campaign History" action opens its own nested anchored popover, portaled
      // to <body> as a sibling of this popup — exempt clicks inside it so picking a
      // status filter or a campaign link doesn't also close this outer popup.
      if ((node as Element)?.closest?.('.campaign-history-popover')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown, true);
    return () => document.removeEventListener('mousedown', onDocDown, true);
  }, [open, isClickMode]);

  useEffect(() => {
    if (!open || !isClickMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, isClickMode]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    const host = document.createElement('div');
    host.setAttribute('data-channel-links-popover-root', 'true');
    portalHostRef.current = host;
    document.body.appendChild(host);
    setPortalReady(true);

    return () => {
      setPortalReady(false);
      if (host.parentNode) {
        host.parentNode.removeChild(host);
      }
      if (portalHostRef.current === host) {
        portalHostRef.current = null;
      }
    };
  }, []);

  if (!userId) {
    return <>{children}</>;
  }

  const triggerChildren = isClickMode
    ? Children.map(children, (child) => {
        if (!isValidElement(child)) return child;
        const el = child as ReactElement<{ onClick?: (e: MouseEvent) => void }>;
        const prevOnClick = el.props.onClick;
        return cloneElement(el, {
          onClick: (e: MouseEvent) => {
            prevOnClick?.(e);
            if (e.defaultPrevented) return;
            clearCloseTimer();
            updatePosition();
            setOpen((prev) => !prev);
            e.preventDefault();
          },
        });
      })
    : children;

  return (
    <div
      ref={wrapRef}
      className={`channel-popover-wrap ${className ?? ''}`}
      onMouseEnter={isClickMode ? undefined : handleOpen}
      onMouseLeave={isClickMode ? undefined : handleCloseSoon}
      onFocus={isClickMode ? undefined : handleOpen}
      onBlur={isClickMode ? undefined : handleCloseSoon}
    >
      {triggerChildren}
      {open && portalReady && portalHostRef.current && createPortal(
        <div
          ref={portalFloatRef}
          className="clp-float"
          style={{ top: position.top, left: position.left }}
          onMouseEnter={isClickMode ? undefined : handleOpen}
          onMouseLeave={isClickMode ? undefined : handleCloseSoon}
        >
          <div className="clp-card ggc-popover-pop-in">
            <div className="clp-header">
              <div className="clp-header__label">
                {t('Channel links', '채널 링크')}
              </div>
              <div className="clp-header__row">
                <ChannelThumbnail
                  src={thumbnailUrl}
                  className="clp-header__thumb"
                  fallbackClassName="clp-header__thumb-fallback"
                />
                <div className="clp-header__info">
                  <div className="clp-header__name-row">
                    {channelType && <ChannelTypeIcon type={channelType} size={14} />}
                    {primarySocialUrl ? (
                      <a
                        href={primarySocialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="clp-header__name"
                        title={t('Open social link', '소셜 링크 열기')}
                        onClick={() => onLinkClick?.()}
                      >
                        {channelName || '-'}
                      </a>
                    ) : (
                      <div className="clp-header__name">
                        {channelName || '-'}
                      </div>
                    )}
                    {primarySocialUrl && (
                      <>
                        <a
                          href={primarySocialUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="clp-header__external"
                          title={t('Open social link', '소셜 링크 열기')}
                          onClick={() => onLinkClick?.()}
                        >
                          <i className="fa fa-external-link" />
                        </a>
                        <button
                          type="button"
                          className="clp-header__copy-btn"
                          title={t('Copy URL', 'URL 복사')}
                          onClick={(e) => handleCopyUrl(e, primarySocialUrl)}
                        >
                          <i className={`fa ${urlCopied ? 'fa-check' : 'fa-copy'}`} />
                        </button>
                      </>
                    )}
                  </div>
                  {subtitle && (
                    <div className="clp-header__subtitle">
                      {subtitle}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 내부 관리자 링크 (채팅 시스템 · 채널 상세 · 채널 수정 · 통계 보기 · 캠페인 이력) */}
            {showInternalActions && (
            <div className="clp-actions">
              <div className="clp-actions__title">{t('Channel  shortcut button options', '채널명에 바로가기 버튼 표시 옵션')}</div>
              <span className="clp-action">
                <Tooltip content={toggleTip}>
                  <span className="clp-action__toggle inline-flex">
                    <GgcCheckbox
                      checked={nameColPrefs.chat}
                      onChange={() => toggleNameColumnButton('chat')}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </span>
                </Tooltip>
                <i className="fa fa-comments clp-action__icon clp-action__icon--chat" />
                <a
                  href={resolvedChatHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="clp-action__link clp-action__link--chat"
                  onClick={(e) => {
                    onLinkClick?.();
                    if (onChatSystemClick) {
                      e.preventDefault();
                      onChatSystemClick(userId);
                      if (isClickMode) setOpen(false);
                    }
                  }}
                >
                  {t('Chat System', '채팅 시스템')}
                  <i className="fa fa-columns clp-action__drawer-icon" />
                </a>
              </span>
              <span className="clp-action">
                <Tooltip content={toggleTip}>
                  <span className="clp-action__toggle inline-flex">
                    <GgcCheckbox
                      checked={nameColPrefs.details}
                      onChange={() => toggleNameColumnButton('details')}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </span>
                </Tooltip>
                <i className="fa fa-user clp-action__icon clp-action__icon--details" />
                <a
                  href={resolvedDetailsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="clp-action__link clp-action__link--details"
                  onClick={(e) => {
                    onLinkClick?.();
                    if (onChannelDetailsClick) {
                      e.preventDefault();
                      onChannelDetailsClick(userId);
                      if (isClickMode) setOpen(false);
                    }
                  }}
                >
                  {t('Channel Details', '채널 상세')}
                  <i className="fa fa-columns clp-action__drawer-icon" />
                </a>
              </span>
              {/* Stats before Edit — matches the actual quick-button row order
                  (InfluencerRow.tsx: chat/details/stats/edit/history), which used to
                  differ from this list's own order. */}
              <span className="clp-action">
                <Tooltip content={toggleTip}>
                  <span className="clp-action__toggle inline-flex">
                    <GgcCheckbox
                      checked={nameColPrefs.stats}
                      onChange={() => toggleNameColumnButton('stats')}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </span>
                </Tooltip>
                <i className="fa fa-line-chart clp-action__icon clp-action__icon--stats" />
                <a
                  href={`/influencer/${userId}/stats`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="clp-action__link clp-action__link--stats"
                  onClick={(e) => {
                    onLinkClick?.();
                    if (onStatsClick) {
                      e.preventDefault();
                      onStatsClick(userId);
                      if (isClickMode) setOpen(false);
                    }
                  }}
                >
                  {t('Open stats', '통계 보기')}
                  <i className="fa fa-columns clp-action__drawer-icon" />
                </a>
              </span>
              <span className="clp-action">
                <Tooltip content={toggleTip}>
                  <span className="clp-action__toggle inline-flex">
                    <GgcCheckbox
                      checked={nameColPrefs.edit}
                      onChange={() => toggleNameColumnButton('edit')}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </span>
                </Tooltip>
                <i className={`fa ${editLoading ? 'fa-spinner fa-spin' : 'fa-pencil'} clp-action__icon clp-action__icon--edit`} />
                <button
                  type="button"
                  className="clp-action__link clp-action__link--edit"
                  disabled={editLoading}
                  onClick={async () => {
                    if (editLoading) return;
                    onLinkClick?.();
                    // Keep the popover open so the spinner is visible during the load;
                    // close it once the action resolves (dialog opens or load fails).
                    setEditLoading(true);
                    try {
                      if (onChannelEditClick) {
                        // Caller override (e.g. dashboard drawer edit).
                        await onChannelEditClick(userId);
                      } else {
                        // Default: load the user and open the shared EditChannelDialog,
                        // so every page exposes the same "Edit Channel" action.
                        const res = await loadPanelUser<{ user: EditChannelDialogProps['user'] }>(userId);
                        setEditUser(res.user);
                      }
                    } catch {
                      dispatch(showSnackbar({
                        text: t('Failed to load channel details', '채널 정보 로드 실패'),
                        kind: 'api-error',
                        timestamp: Date.now(),
                      }));
                    } finally {
                      setEditLoading(false);
                      if (isClickMode) setOpen(false);
                    }
                  }}
                >
                  {editLoading ? t('Loading...', '로딩 중...') : t('Edit Channel', '채널 수정')}
                </button>
              </span>
              <CampaignHistoryPopover
                onLoadItems={() => loadCampaignHistoryItems(userId)}
                renderTrigger={({ loading: historyLoading, toggle }) => (
                  <span className="clp-action">
                    <Tooltip content={toggleTip}>
                      <span className="clp-action__toggle inline-flex">
                        <GgcCheckbox
                          checked={nameColPrefs.history}
                          onChange={() => toggleNameColumnButton('history')}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </span>
                    </Tooltip>
                    <i className={`fa ${historyLoading ? 'fa-spinner fa-spin' : 'fa-history'} clp-action__icon clp-action__icon--history`} />
                    <button
                      type="button"
                      className="clp-action__link clp-action__link--history"
                      disabled={historyLoading}
                      onClick={() => { onLinkClick?.(); toggle(); }}
                    >
                      {historyLoading ? t('Loading...', '로딩 중...') : t('Campaign History', '캠페인 이력')}
                      {!historyLoading && campaignHistoryCount != null && (
                        <span className="clp-action__count">{campaignHistoryCount}</span>
                      )}
                    </button>
                  </span>
                )}
              />
            </div>
            )}
          </div>
        </div>,
        portalHostRef.current,
      )}
      {editUser && (
        <EditChannelDialog
          open
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={onChannelSaved}
        />
      )}
    </div>
  );
}
